// FileFormatUtils.js
// This file contains a class with utility constants and functions to handle file formats and lists of directories and files.

// Copyright HS Analysis GmbH, 2024
// Author: Viktor Eberhardt

import Backend from "./Backend";
import { SortByType } from "../../fileViewer/FileViewer";

class FileFormatUtils {
  /**
   * The supportedFormats object defines the supported file formats for different categories.
   * Each category is represented by a key, and the corresponding value is an array of file extensions.
   *
   * @type {Object}
   * @property {string[]} text - The supported file extensions for text files.
   * @property {string[]} ESR - The supported file extensions for ESR files.
   * @property {string[]} Audio - The supported file extensions for audio files.
   * @property {string[]} Viewer - The supported file extensions for image files.
   */
  static supportedFormats = {
    text: ["txt"],
    ESR: ["csv"],
    Audio: ["mp3", "wav"],
    Viewer: [],
  };

  /**
   * Retrieves scene information for a list of file paths.
   *
   * For each file path provided, this function fetches associated scene IDs and file metadata,
   * then combines them into a list of scene objects containing relevant details.
   *
   * @async
   * @param {string[]} files - An array of file paths to process.
   * @returns {Promise<Object[]>} A promise that resolves to an array of scene objects, each containing:
   *   - {Object} scene - Scene data from the backend.
   *   - {string} path - The relative file path.
   *   - {string|Date} creationTime - The file's creation time.
   *   - {number} sceneCount - The number of scenes associated with the file.
   *   - {string} name - The file name.
   */
  static async getScenesForFile(files) {
    files = files.map((file) => file.replace(/\\/g, "/"));
    let results = await Backend.getFileIdData(files);
    const tempSceneObj = {};

    results.forEach((sceneData, index) => {
      const path = files[index];
      if (sceneData && sceneData.sceneIds.length > 0) {
        tempSceneObj[path] = sceneData.sceneIds;
      }
    });

    const pathList = await Promise.all(
      files.map((path) => Backend.getFileInfoByPath(path))
    );
    const sceneList = { ...tempSceneObj };
    let list = [];
    for (const pathItem of pathList) {
      const sceneItem = sceneList[pathItem.relativePath.replace(/\\/g, "/")];
      if (!sceneItem) continue;
      for (const scene of sceneItem) {
        list.push({
          ...scene,
          path: pathItem.relativePath,
          creationTime: pathItem.creationTime,
          sceneCount: sceneItem.length,
          name: pathItem.fileName,
        });
      }
    }
    return list;
  }

  /**
   * Refreshes the supported image formats by fetching the supported image formats from the backend.
   */
  static refreshSupportedImageFormats() {
    Backend.getSupportedImageFormats().then((formats) => {
      FileFormatUtils.supportedFormats.Viewer = formats;
    });
  }

  /**
   * Filters the given array of entities to only include files with supported extensions for viewing.
   * Directories with a corresponding .mrxs file are excluded.
   *
   * @param {Array} entities - The array of entities to filter.
   * @returns {Array} - The filtered array of entities.
   */
  static filterViewerEntities(entities) {
    // Guard: ensure we always work with an array to avoid "entities.filter is not a function"
    const list = Array.isArray(entities) ? entities : [];

    if (list.length === 0) return [];

    // Normalize supported formats to lowercase for reliable comparison
    const viewerFormats = Array.isArray(
      FileFormatUtils.supportedFormats?.Viewer
    )
      ? FileFormatUtils.supportedFormats.Viewer.map((ext) =>
          typeof ext === "string" ? ext.toLowerCase() : ext
        )
      : [];

    return list.filter((entity) => {
      if (!entity || !entity.type) return false;

      if (entity.type === "file") {
        // get the extension from entity.path
        const path = entity.path || "";
        const parts = path.split(".");
        const entityExtension =
          parts.length > 1 ? parts.pop().toLowerCase() : "";
        // check if the extension is in supportedFormats.Viewer
        return viewerFormats.includes(entityExtension);
      }

      if (entity.type === "directory") {
        const dirPath = entity.path || "";
        return !list.some(
          (element) => (element?.path || "") === `${dirPath}.mrxs`
        );
      }

      return false;
    });
  }

  /**
   * Sorts an array of entities by type and name.
   * directories come before files, and entities are sorted by name.
   *
   * @param {Array} entities - The array of entities to be sorted.
   * @param {string} sortBy - The criteria to sort by (e.g., "date").
   * @param {boolean} sortReverse - Whether to reverse the sorted array.
   * @returns {Array} - The sorted array of entities.
   */
  static sortEntitiesByTypeAndName(
    entities,
    sortBy = "",
    folderSortReverse = false,
    fileSortReverse = false
  ) {
    // Separate directories and files
    const directories = entities.filter((e) => e.type === "directory");
    const files = entities.filter((e) => e.type !== "directory");

    // Sorting function
    const sortFn = (a, b) => {
      if (
        sortBy === SortByType.Date &&
        a.creationTime &&
        b.creationTime &&
        a.creationTime !== b.creationTime
      ) {
        return a.creationTime > b.creationTime ? 1 : -1;
      }

      // Sort by additionalName (fall back to name/path) if requested
      if (sortBy === SortByType.AdditionalName) {
        const aKey = (a.additionalName || a.name || a.path || "")
          .toString()
          .toLowerCase();
        const bKey = (b.additionalName || b.name || b.path || "")
          .toString()
          .toLowerCase();
        if (aKey !== bKey) return aKey.localeCompare(bKey);
      }

      // Sort by file extension if requested
      if (sortBy === SortByType.Type) {
        const extA = a.path.split(".").pop().toLowerCase();
        const extB = b.path.split(".").pop().toLowerCase();
        if (extA !== extB) {
          return extA.localeCompare(extB);
        }
      }

      /**
       * Finds the longest common prefix string between two input strings.
       *
       * @param {string} str1 - The first string to compare.
       * @param {string} str2 - The second string to compare.
       * @returns {string} The longest common prefix shared by both strings.
       */
      const findCommonPrefix = (str1, str2) => {
        let i = 0;
        while (i < str1.length && i < str2.length && str1[i] === str2[i]) {
          i++;
        }
        return str1.substring(0, i);
      };

      const commonPrefix = findCommonPrefix(a.path, b.path);
      const pathA = a.path.substring(commonPrefix.length);
      const pathB = b.path.substring(commonPrefix.length);

      /**
       * Extracts the first sequence of digits from a string and returns it as an integer.
       *
       * @param {string} str - The input string to search for numbers.
       * @returns {number|null} The first number found in the string as an integer, or null if no numbers are found.
       */
      const extractNumbers = (str) => {
        const match = str.match(/\d+/);
        return match ? parseInt(match[0], 10) : null;
      };

      const numA = extractNumbers(pathA);
      const numB = extractNumbers(pathB);

      if (numA !== null && numB !== null) {
        return numA - numB;
      }

      return a.path.localeCompare(b.path);
    };

    directories.sort(sortFn);
    files.sort(sortFn);

    if (folderSortReverse) {
      directories.reverse();
    }

    if (fileSortReverse) {
      files.reverse();
    }

    // Directories always before files
    return [...directories, ...files];
  }
}

export default FileFormatUtils;
